import { ReactElement, ReactNode, useState } from 'react';

import { Timestamp } from 'firebase/firestore';

import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import dayjs, { Dayjs } from 'dayjs';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

import { FromDayJS, ToDayJS } from '../../utils/utils';

export type GratiDateTimeFieldDefaultTime = 'start' | 'end';

interface GratiDateTimeFieldProps {
  label: string;
  value: Timestamp | null;
  /** The timezone the date and time are read and set in — the event's. */
  timezone?: string;
  /** The time a date gets until one is set: the start or the end of its day. */
  defaultTime: GratiDateTimeFieldDefaultTime;
  onChange: (value: Timestamp) => void;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  /** More to say under the field, after "End of day · Set time", e.g. what it follows. */
  note?: ReactNode;
}

const atDefaultTime = (date: Dayjs, defaultTime: GratiDateTimeFieldDefaultTime): Dayjs =>
  defaultTime === 'start' ? date.startOf('day') : date.endOf('day');

const isAtDefaultTime = (date: Dayjs, defaultTime: GratiDateTimeFieldDefaultTime): boolean =>
  defaultTime === 'start'
    ? date.hour() === 0 && date.minute() === 0 && date.second() === 0
    : date.hour() === 23 && date.minute() === 59 && date.second() >= 59;

/**
 * A date that's usually a whole day — it takes effect at the start or the end of the day — with a
 * time only when the admin sets one: "End of day · Set time" under the date, and a time picker with
 * "Clear custom time" once set. Read and set in the event's timezone. Sign up opens/closes and
 * when players' choices close.
 */
export default function GratiDateTimeField({
  label,
  value,
  timezone,
  defaultTime,
  onChange,
  minDate,
  maxDate,
  note,
}: GratiDateTimeFieldProps): ReactElement {
  const current = value ? ToDayJS(value, timezone) : null;
  const [isTimeShown, setIsTimeShown] = useState(() => !!current && !isAtDefaultTime(current, defaultTime));

  const handleDateChange = (date: Dayjs | null) => {
    if (!date?.isValid()) return;
    // Re-read the picked calendar date in the event's timezone, so start/end of day is its midnight
    const dateInTz = timezone ? dayjs.tz(date.format('YYYY-MM-DD'), timezone) : date;
    const next = isTimeShown && current
      ? dateInTz.hour(current.hour()).minute(current.minute()).second(current.second())
      : atDefaultTime(dateInTz, defaultTime);
    onChange(FromDayJS(next));
  };

  const handleClearTime = () => {
    if (current) onChange(FromDayJS(atDefaultTime(current, defaultTime)));
    setIsTimeShown(false);
  };

  return (
    <Stack spacing={1}>
      <Stack>
        <DatePicker
          label={label}
          value={current}
          minDate={minDate}
          maxDate={maxDate}
          onChange={handleDateChange}
          slotProps={{ textField: { size: 'small' } }}
        />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {!isTimeShown && (
            <>
              {defaultTime === 'start' ? 'Start of day' : 'End of day'} &middot;{' '}
              <Link component="button" variant="caption" onClick={() => setIsTimeShown(true)}>
                Set time
              </Link>
            </>
          )}
          {!isTimeShown && note && <> &middot; </>}
          {note}
        </Typography>
      </Stack>
      {isTimeShown && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <TimePicker
            label={`${label} at`}
            value={current}
            onChange={(t) => { if (t?.isValid()) onChange(FromDayJS(t)); }}
            slotProps={{ textField: { size: 'small' } }}
          />
          <Link component="button" variant="body2" onClick={handleClearTime}>
            Clear custom time
          </Link>
        </Stack>
      )}
    </Stack>
  );
}
